import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { client, urlFor } from '../lib/sanity';
import { PortableText } from '@portabletext/react';
import * as LucideIcons from 'lucide-react';
import Button from './ui/Button';

const isInternalLink = (url) => typeof url === 'string' && url.startsWith('/');

const iconMap = {
    Rocket: LucideIcons.Rocket,
    Lightbulb: LucideIcons.Lightbulb,
    Zap: LucideIcons.Zap,
    Target: LucideIcons.Target,
    Heart: LucideIcons.Heart,
    Shield: LucideIcons.Shield,
    Code: LucideIcons.Code,
    Users: LucideIcons.Users,
    Briefcase: LucideIcons.Briefcase,
    Award: LucideIcons.Award,
    Cpu: LucideIcons.Cpu,
    Database: LucideIcons.Database,
    Globe: LucideIcons.Globe,
    Layers: LucideIcons.Layers,
    PackageCheck: LucideIcons.PackageCheck,
    Star: LucideIcons.Star,
    TrendingUp: LucideIcons.TrendingUp,
};

const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName];
    if (!IconComponent) return null;
    return <IconComponent size={24} className="text-accent" />;
};

const CustomPortableTextComponents = {
    types: {
        image: ({ value }) => (
            <div className="my-6 border-2 border-black dark:border-white overflow-hidden">
                <img
                    src={urlFor(value).url()}
                    alt={value.alt || 'Image'}
                    className="w-full h-auto object-cover"
                />
            </div>
        ),
        videoEmbed: ({ value }) => (
            <div className="my-6 border-2 border-black dark:border-white overflow-hidden w-full">
                <iframe
                    width="100%"
                    height="400"
                    src={value.url}
                    title={value.title || 'Embedded video'}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full"
                />
            </div>
        ),
    },
    block: {
        h1: ({ children }) => <h1 className="text-4xl font-serif font-bold my-4 text-black dark:text-white">{children}</h1>,
        h2: ({ children }) => <h2 className="text-3xl font-serif font-bold my-3 text-black dark:text-white">{children}</h2>,
        h3: ({ children }) => <h3 className="text-2xl font-serif font-bold my-2 text-black dark:text-white">{children}</h3>,
        normal: ({ children }) => <p className="text-lg text-zinc-700 dark:text-zinc-300 my-3 leading-relaxed">{children}</p>,
    },
    marks: {
        strong: ({ children }) => <strong className="font-bold text-black dark:text-white">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
    },
};

export default function ScrollMore() {
    const [scrollMoreData, setScrollMoreData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();

        client
            .fetch(
                `*[_type == "scrollMore"][0]{
                    title,
                    intro,
                    description,
                    "profileImage": profileImage.asset->url,
                    highlights[] {
                        title,
                        description,
                        icon
                    },
                    ctaText,
                    ctaLink
                }`,
                {},
                { signal: controller.signal }
            )
            .then((data) => {
                setScrollMoreData(data);
                setLoading(false);
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setError(err?.message || 'Failed to load content');
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, []);

    if (loading) {
        return (
            <section className="py-16 md:py-24 bg-white dark:bg-zinc-950">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse">
                        <div className="h-12 bg-zinc-200 dark:bg-zinc-800 w-3/4 mb-4" />
                        <div className="h-6 bg-zinc-200 dark:bg-zinc-800 w-1/2 mb-6" />
                        <div className="space-y-3">
                            <div className="h-4 bg-zinc-200 dark:bg-zinc-800 w-full" />
                            <div className="h-4 bg-zinc-200 dark:bg-zinc-800 w-5/6" />
                            <div className="h-4 bg-zinc-200 dark:bg-zinc-800 w-4/5" />
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !scrollMoreData) {
        return null; // Silently fail if no data
    }

    const { title, intro, description, profileImage, highlights, ctaText, ctaLink } = scrollMoreData;

    return (
        <section className="py-16 md:py-24 bg-white dark:bg-zinc-950">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Title */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="mb-12"
                >
                    <h2 className="text-4xl md:text-5xl font-serif font-bold text-black dark:text-white mb-4">
                        {title || 'About Me'}
                    </h2>
                    <div className="w-16 h-1.5 bg-accent" />
                </motion.div>

                {/* Content Grid */}
                <div className="grid md:grid-cols-12 gap-12 md:gap-8 items-start mb-16">
                    {/* Left: Intro and Image */}
                    <motion.div
                        className="md:col-span-5"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        {profileImage && (
                            <div className="mb-8 border-2 border-black dark:border-white overflow-hidden">
                                <img
                                    src={profileImage}
                                    alt="Profile"
                                    className="w-full h-auto object-cover aspect-square"
                                />
                            </div>
                        )}
                        <p className="text-lg text-zinc-700 dark:text-zinc-300 leading-relaxed font-medium">
                            {intro}
                        </p>
                    </motion.div>

                    {/* Right: Highlights */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="space-y-6 md:col-span-7 md:mt-12"
                    >
                        {highlights && highlights.length > 0 && (
                            <div className="space-y-4">
                                {highlights.map((highlight, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                        className="p-4 border-2 border-black dark:border-white hover:shadow-brutal transition-shadow"
                                    >
                                        <div className="flex gap-3">
                                            {highlight.icon && (
                                                <div className="flex-shrink-0 mt-1">
                                                    {getIcon(highlight.icon)}
                                                </div>
                                            )}
                                            <div>
                                                <h3 className="font-bold text-black dark:text-white">
                                                    {highlight.title}
                                                </h3>
                                                <p className="text-sm text-zinc-700 dark:text-zinc-400 mt-1">
                                                    {highlight.description}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </div>

                {/* Full Description */}
                {description && description.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="prose prose-lg dark:prose-invert max-w-none mb-12"
                    >
                        <PortableText
                            value={description}
                            components={CustomPortableTextComponents}
                        />
                    </motion.div>
                )}

                {/* CTA Button */}
                {ctaText && ctaLink && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="flex justify-center"
                    >
                        {isInternalLink(ctaLink) ? (
                            <Button as={Link} to={ctaLink} className="px-8 py-4">
                                {ctaText}
                            </Button>
                        ) : (
                            <Button as="a" href={ctaLink} target="_blank" rel="noopener noreferrer" className="px-8 py-4">
                                {ctaText}
                            </Button>
                        )}
                    </motion.div>
                )}
            </div>
        </section>
    );
}
